import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';


export default function AboutPage() {
  const [activeTab, setActiveTab] = useState<'intro' | 'reputation' | 'contact'>('intro');
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '', website_alt: '' });
  const [formStatus, setFormStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '关于我们' },
  ];

  const tabs = [
    { key: 'intro' as const, label: '品牌介绍', icon: 'ri-building-2-line' },
    { key: 'reputation' as const, label: '口碑评价', icon: 'ri-thumb-up-line' },
    { key: 'contact' as const, label: '联系我们', icon: 'ri-mail-line' },
  ];

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (contactForm.website_alt.trim()) {
      setFormStatus({ type: 'success', message: '感谢您的留言，我们会尽快回复！' });
      return;
    }
    if (contactForm.message.length > 500) {
      setFormStatus({ type: 'error', message: '留言内容不能超过500个字符' });
      return;
    }
    setFormSubmitting(true);
    setFormStatus(null);
    try {
      const formUrl = 'https://readdy.ai/api/form/d9od0tgbdkpk99j7gui0';
      const formEl = e.currentTarget as HTMLFormElement;
      const formData = new FormData(formEl);
      formData.delete('website_alt');
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || '';
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus({ type: 'success', message: '感谢您的留言，我们会尽快回复！' });
        setContactForm({ name: '', email: '', message: '', website_alt: '' });
      } else if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
        setFormStatus({ type: 'error', message: '提交失败，请稍后再试。' });
      } else {
        setFormStatus({ type: 'error', message: serverMsg || '提交失败，请稍后再试。' });
      }
    } catch {
      setFormStatus({ type: 'error', message: '网络错误，请重试。' });
    } finally {
      setFormSubmitting(false);
    }
  };

  const schemaOrg = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于90影院',
    description: '90影院是一家专注于提供最新电影、电视剧、综艺、动漫等影视内容的在线平台。汇聚8090影院电影、电视剧、综艺、动漫等最新热播影视资源。',
    url: 'https://www.90cinema.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '90影院',
      url: 'https://www.90cinema.com/',
    },
    about: {
      '@type': 'Organization',
      name: '90影院',
      foundingDate: '2018',
      description: '90影院是一家专注于提供最新电影、电视剧、综艺、动漫等影视内容的在线平台。',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '90影院提供哪些类型的影视内容？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '90影院提供电影、电视剧、综艺、动漫等四大类影视内容，涵盖最新院线大片、热播剧集、热门综艺和精彩动漫番剧。',
        },
      },
      {
        '@type': 'Question',
        name: '90影院的影视资源更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '90影院的内容团队每日更新，确保用户能够第一时间观看到最新的影视作品。热播连载剧集和综艺节目会在播出后尽快更新。',
        },
      },
      {
        '@type': 'Question',
        name: '90影院是否支持高清播放？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，90影院致力于为用户提供高清流畅的观影体验，大部分影视内容均支持高清画质，播放不缓冲。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在90影院找到想看的影视内容？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过首页的分类导航进入电影、电视剧、综艺、动漫页面浏览，也可以使用网站顶部的搜索功能，输入影视名称快速查找您想看的内容。',
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(schemaOrg)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <Breadcrumb items={breadcrumbItems} />

        <main>
          <section className="w-full px-4 md:px-6 py-6 md:py-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl">
                    <i className="ri-play-circle-fill text-2xl text-background-50"></i>
                  </div>
                </div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  关于90影院
                </h1>
                <p className="text-foreground-500 text-sm md:text-base max-w-lg mx-auto">
                  汇聚8090影院电影、电视剧、综艺、动漫等最新热播影视，为您带来极致观影体验
                </p>
              </div>

              <div className="flex items-center justify-center mb-8">
                <div className="inline-flex items-center bg-background-100 rounded-full p-1">
                  {tabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        activeTab === tab.key
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-600 hover:text-foreground-900'
                      }`}
                    >
                      <i className={`${tab.icon} text-xs`}></i>
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {activeTab === 'intro' && (
                <div className="space-y-6">
                  <div className="bg-background-100 rounded-xl p-6 md:p-8">
                    <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                      品牌故事
                    </h2>
                    <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                      <p>
                        <strong>90影院</strong>成立于2018年，是专注于为中文用户提供优质影视内容发现与浏览体验的在线平台。经过多年发展，我们已积累了海量的影视资源库，涵盖<strong>电影</strong>、<strong>电视剧</strong>、<strong>综艺</strong>、<strong>动漫</strong>等多个类别。
                      </p>
                      <p>
                        90影院秉持"让每个人都能轻松找到想看的内容"的理念，致力于打造一个内容全面、分类清晰、体验流畅的影视导航平台。无论是追最新院线大片、热门电视剧，还是寻找经典老片、小众佳作，90影院都能满足您的需求。
                      </p>
                      <p>
                        我们的内容团队每日更新，紧跟影视行业最新动态，确保用户能够在第一时间获取最新热播资源。8090影院作为旗下品牌，同样致力于为80后、90后用户群体提供精准的影视内容推荐。
                      </p>
                      <p>
                        90影院的核心理念是：<strong>内容为王，体验至上</strong>。我们不断优化网站功能和界面设计，让每一位访问者都能享受到流畅、舒适的浏览体验。
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-background-100 rounded-xl p-6 text-center">
                      <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-3">
                        <i className="ri-film-line text-xl"></i>
                      </div>
                      <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">海量资源</h3>
                      <p className="text-xs text-foreground-500">覆盖电影、电视剧、综艺、动漫四大类别，持续更新</p>
                    </div>
                    <div className="bg-background-100 rounded-xl p-6 text-center">
                      <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-3">
                        <i className="ri-speed-line text-xl"></i>
                      </div>
                      <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">高清流畅</h3>
                      <p className="text-xs text-foreground-500">高清画质不缓冲，带来极致观影享受</p>
                    </div>
                    <div className="bg-background-100 rounded-xl p-6 text-center">
                      <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 mb-3">
                        <i className="ri-refresh-line text-xl"></i>
                      </div>
                      <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">每日更新</h3>
                      <p className="text-xs text-foreground-500">紧跟影视动态，第一时间获取最新内容</p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'reputation' && (
                <div className="space-y-6">
                  <div className="bg-background-100 rounded-xl p-6 md:p-8">
                    <h2 className="text-lg font-heading font-bold text-foreground-900 mb-6">
                      用户口碑
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        { name: '追剧达人小王', rating: 5, text: '90影院的资源更新速度真的快！每部想追的剧都能第一时间找到，画质也很清晰，看剧体验一级棒。', date: '2026-07-15' },
                        { name: '电影爱好者老李', rating: 5, text: '分类很清晰，找电影特别方便。8090影院的电影库很全，从经典老片到最新院线都有。', date: '2026-07-10' },
                        { name: '动漫迷小陈', rating: 4, text: '动漫资源很丰富，国漫日漫都有，每周更新也很及时。希望能增加更多经典番剧。', date: '2026-06-28' },
                        { name: '综艺控张姐', rating: 5, text: '综艺板块做得很用心，热门综艺都有收录，周末宅家看综艺的首选平台。', date: '2026-06-20' },
                      ].map((review, index) => (
                        <div key={index} className="bg-background-50 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs font-semibold">
                              {review.name.charAt(0)}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground-800">{review.name}</p>
                              <div className="flex items-center gap-0.5">
                                {Array.from({ length: 5 }).map((_, i) => (
                                  <i
                                    key={i}
                                    className={`text-xs ${i < review.rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-300'}`}
                                  ></i>
                                ))}
                              </div>
                            </div>
                          </div>
                          <p className="text-sm text-foreground-600 leading-relaxed">{review.text}</p>
                          <p className="text-xs text-foreground-400 mt-2">{review.date}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-background-100 rounded-xl p-6 md:p-8">
                    <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                      数据说话
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {[
                        { value: '10万+', label: '日均访问用户' },
                        { value: '5000+', label: '影视资源总量' },
                        { value: '98%', label: '用户满意度' },
                        { value: '4.8', label: '平均评分' },
                      ].map((stat, index) => (
                        <div key={index} className="text-center p-4">
                          <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600 mb-1">
                            {stat.value}
                          </div>
                          <div className="text-xs text-foreground-500">{stat.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'contact' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-background-100 rounded-xl p-6 md:p-8">
                      <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                        联系方式
                      </h2>
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                            <i className="ri-mail-line"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground-800">电子邮箱</p>
                            <p className="text-sm text-foreground-500">service@90cinema.com</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                            <i className="ri-customer-service-line"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground-800">客服热线</p>
                            <p className="text-sm text-foreground-500">400-888-9090</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                            <i className="ri-time-line"></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground-800">工作时间</p>
                            <p className="text-sm text-foreground-500">周一至周日 9:00 - 21:00</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-background-100 rounded-xl p-6 md:p-8">
                      <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                        留言反馈
                      </h2>
                      <form onSubmit={handleContactSubmit} data-readdy-form="">
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm text-foreground-600 mb-1">姓名</label>
                            <input
                              type="text"
                              name="name"
                              value={contactForm.name}
                              onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                              required
                              className="w-full h-10 px-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
                              placeholder="请输入您的姓名"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-foreground-600 mb-1">邮箱</label>
                            <input
                              type="email"
                              name="email"
                              value={contactForm.email}
                              onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                              required
                              className="w-full h-10 px-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
                              placeholder="请输入您的邮箱"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-foreground-600 mb-1">
                              留言内容 <span className="text-foreground-400">({contactForm.message.length}/500)</span>
                            </label>
                            <textarea
                              name="message"
                              value={contactForm.message}
                              onChange={(e) => {
                                if (e.target.value.length <= 500) {
                                  setContactForm({ ...contactForm, message: e.target.value });
                                }
                              }}
                              required
                              rows={4}
                              maxLength={500}
                              className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 resize-none"
                              placeholder="请输入您的留言（最多500字）"
                            />
                          </div>
                          <input
                            type="text"
                            name="website_alt"
                            value={contactForm.website_alt}
                            onChange={(e) => setContactForm({ ...contactForm, website_alt: e.target.value })}
                            tabIndex={-1}
                            autoComplete="off"
                            aria-hidden="true"
                            readOnly
                            className="honeypot-field"
                          />
                          <button
                            type="submit"
                            disabled={formSubmitting}
                            className="w-full h-10 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                          >
                            {formSubmitting ? '提交中...' : '提交留言'}
                          </button>
                          {formStatus && (
                            <p className={`text-sm ${formStatus.type === 'success' ? 'text-primary-600' : 'text-accent-500'}`}>
                              {formStatus.message}
                            </p>
                          )}
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}